/**
 * SpringArmComponent - manages its own position to avoid collision for attached children.
 * Designed to work in a component hierarchy:
 *   root -> pivot -> springArm -> camera/mesh/etc.
 *
 * The spring arm moves itself along its local Z-axis based on collision detection.
 * Any children attached to the spring arm automatically follow.
 */

import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { CollisionConfig } from '../../systems/collision/CollisionConfig.js';
import { DefaultCollisionProfile } from '../../systems/collision/CollisionTypes.js';
import { property } from '../../utils/serialization/decorator.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';
import { SceneComponent } from '../SceneComponent.js';

import type { SceneComponentOptions } from '../SceneComponent.js';

/**
 * Options for configuring a SpringArmComponent
 */
export interface SpringArmComponentOptions extends SceneComponentOptions {
  /** Small distance to keep between arm end and obstacle when collision happens */
  collisionBuffer?: number;
  /** Optional local-space offset for spring-arm pivot point (relative to parent) */
  pivotOffset?: THREE.Vector3;
  /** Target arm length (distance from pivot). Null uses initial position length. */
  armLength?: number | null;
  /** Damping lambda used when spring-arm returns to desired distance after collision */
  springBackLambda?: number;
  /** Whether to enable collision detection */
  collisionEnabled?: boolean;
}

@EngineClass('SpringArmComponent')
export class SpringArmComponent extends SceneComponent {
  @property({ type: 'number', min: 0, step: 0.01, decimals: 3 })
  public collisionBuffer: number = 0.05;

  @property()
  public pivotOffset: THREE.Vector3 = new THREE.Vector3();

  @property({ type: 'number', min: 0 })
  public armLength: number | null = null;

  @property({ type: 'number', min: 0, step: 0.1, decimals: 2 })
  public springBackLambda: number = 4.5;

  @property()
  public collisionEnabled: boolean = true;

  protected currentDistance: number | null = null;
  protected baselineDistance: number | null = null;

  protected readonly pivotWorldScratch: THREE.Vector3 = new THREE.Vector3();
  protected readonly armDirectionScratch: THREE.Vector3 = new THREE.Vector3();
  protected readonly resolvedPositionScratch: THREE.Vector3 = new THREE.Vector3();
  protected readonly ignoredComponentsScratch: PrimitiveComponent[] = [];

  public override initialize(options?: SpringArmComponentOptions): void {
    super.initialize(options);
    if (options?.pivotOffset !== undefined) this.pivotOffset.copy(options.pivotOffset);
    this.collisionBuffer = options?.collisionBuffer ?? this.collisionBuffer;
    this.armLength = options?.armLength ?? this.armLength;
    this.springBackLambda = options?.springBackLambda ?? this.springBackLambda;
    this.collisionEnabled = options?.collisionEnabled ?? this.collisionEnabled;
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    this.updateSpringArmPosition(deltaTime);
  }

  /**
   * Updates the spring arm's position along its local Z-axis based on collision.
   * Children attached to the spring arm automatically follow.
   */
  protected updateSpringArmPosition(deltaTime: number): void {
    const targetDistance = this.resolveArmLength();
    const resolvedDistance = this.resolveCollisionDistance(targetDistance, deltaTime);

    if (!(this as any).useAbsolutePosition) {
      this.position.set(0, 0, resolvedDistance);
    }
  }

  /**
   * Returns the target arm length.
   * Uses explicit armLength if set, otherwise derives from initial position.
   */
  protected resolveArmLength(): number {
    if (this.armLength !== null) {
      const explicitDistance = Math.max(this.armLength, 0);
      this.baselineDistance = explicitDistance;
      return explicitDistance;
    }

    if (this.baselineDistance !== null) {
      return this.baselineDistance;
    }

    this.baselineDistance = this.position.length();
    return this.baselineDistance;
  }

  /**
   * Resolves the actual arm distance considering collision.
   * Performs a line trace from pivot in the direction of the arm.
   */
  protected resolveCollisionDistance(desiredDistance: number, deltaTime: number): number {
    if (!this.collisionEnabled) {
      this.currentDistance = null;
      return desiredDistance;
    }

    const physicsEngine = this.getPhysicsEngine();
    if (!physicsEngine) {
      this.currentDistance = null;
      return desiredDistance;
    }

    const springArmProfile = CollisionConfig.getInstance().getProfile(DefaultCollisionProfile.SpringArmCamera);
    if (!springArmProfile) {
      this.currentDistance = null;
      return desiredDistance;
    }

    const owner = this.getActor();
    if (!owner) {
      return desiredDistance;
    }

    // Calculate pivot world position
    this.pivotWorldScratch.copy(this.pivotOffset);
    this.parent?.localToWorld(this.pivotWorldScratch);

    // Arm direction is the spring arm's forward direction (local Z)
    this.getWorldDirection(this.armDirectionScratch);

    if (desiredDistance <= 1e-6) {
      this.currentDistance = 0;
      return 0;
    }

    // Collect children to ignore in collision
    this.ignoredComponentsScratch.length = 0;
    for (const child of this.children) {
      if (child instanceof PrimitiveComponent) {
        this.ignoredComponentsScratch.push(child);
      }
    }

    // Perform hit test from pivot outward
    const hitResults = physicsEngine.performHitTest({
      origin: this.pivotWorldScratch,
      direction: this.armDirectionScratch,
      maxDistance: desiredDistance,
      stopOnFirstHit: true,
      collisionProfile: springArmProfile,
      ignoredActors: [owner],
      ignoredComponents: this.ignoredComponentsScratch,
    });

    const hitDistance = hitResults[0]
      ? this.pivotWorldScratch.distanceTo(hitResults[0].hitLocation)
      : desiredDistance;
    const targetDistance = hitResults[0]
      ? Math.max(hitDistance - this.collisionBuffer, 0)
      : desiredDistance;

    if (this.currentDistance === null || !Number.isFinite(this.currentDistance)) {
      this.currentDistance = desiredDistance;
    }

    const dampingDeltaTime = Math.min(deltaTime, 1 / 30);

    if (this.currentDistance > targetDistance) {
      // Retract immediately to prevent clipping
      this.currentDistance = targetDistance;
    } else {
      // Smoothly spring back toward available distance
      this.currentDistance = THREE.MathUtils.damp(
        this.currentDistance,
        targetDistance,
        this.springBackLambda,
        dampingDeltaTime
      );
    }

    return THREE.MathUtils.clamp(this.currentDistance, 0, desiredDistance);
  }

  public getCurrentDistance(): number | null {
    return this.currentDistance;
  }

  public getBaselineDistance(): number | null {
    return this.baselineDistance;
  }

  public resetSpringArm(): void {
    this.currentDistance = null;
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_SpringArm';
  }
}
